/**
 * Bulk Status Update Bar
 *
 * Fixed action bar shown when targets are selected.
 * Provides status dropdown and confirmation for bulk updates. Selecting
 * `client_halted` requires a mandatory note before the confirm action
 * becomes available (PRP §2 C8).
 */

import { useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ConfirmActions,
  HaltedNoteField,
  StartConfirmButton,
  StatusSelect,
} from './bulk-status-update-bar/bulk-status-update-bar.parts'
import { statusDisplayLabel } from './bulk-status-update-bar/bulk-status-update-bar.helpers'

const CLIENT_HALTED_STATUS = 'client_halted'

interface BulkStatusUpdateBarProps {
  selectedCount: number
  onUpdate: (status: string, note?: string) => void
  updating?: boolean
}

export function BulkStatusUpdateBar({
  selectedCount,
  onUpdate,
  updating,
}: BulkStatusUpdateBarProps): ReactElement | null {
  const { t } = useTranslation('cases')
  const [newStatus, setNewStatus] = useState<string>('')
  const [note, setNote] = useState<string>('')
  const [confirming, setConfirming] = useState(false)

  if (selectedCount === 0) return null

  const requiresNote = newStatus === CLIENT_HALTED_STATUS
  const noteMissing = requiresNote && note.trim().length === 0

  const handleConfirm = (): void => {
    if (!newStatus || noteMissing) return
    onUpdate(newStatus, requiresNote ? note.trim() : undefined)
    setConfirming(false)
    setNewStatus('')
    setNote('')
  }

  return (
    <div className="fixed right-0 bottom-0 left-0 z-40 border-t border-slate-200 bg-white px-6 py-3 shadow-lg dark:border-slate-700 dark:bg-slate-900">
      <div className="items-centre mx-auto flex max-w-7xl flex-wrap justify-between gap-3">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('collections.targetMonitoring.selected', { count: selectedCount })}
        </span>
        <div className="items-centre flex flex-wrap justify-end gap-3">
          <StatusSelect newStatus={newStatus} onChange={setNewStatus} t={t} />
          {requiresNote && <HaltedNoteField note={note} onChange={setNote} t={t} />}
          {confirming ? (
            <ConfirmActions
              selectedCount={selectedCount}
              statusLabel={newStatus ? statusDisplayLabel(newStatus) : ''}
              updating={updating}
              disabled={noteMissing}
              onConfirm={handleConfirm}
              onCancel={() => {
                setConfirming(false)
              }}
              t={t}
            />
          ) : (
            <StartConfirmButton
              newStatus={newStatus}
              updating={updating}
              disabled={noteMissing}
              onClick={() => {
                setConfirming(true)
              }}
              t={t}
            />
          )}
        </div>
      </div>
    </div>
  )
}
